import { useState, useEffect, useCallback, useRef } from 'react';
import { InfrastructureData, InfrastructureSummary, InfrastructureError } from '../types';
import { awsService, gcpService, githubService } from '../services';

interface UseInfrastructureDataOptions {
  autoRefresh?: boolean;
  refreshInterval?: number;
}

interface UseInfrastructureDataReturn {
  data: InfrastructureData | null;
  loading: boolean;
  error: InfrastructureError | null;
  refetch: () => Promise<void>;
  lastUpdated: Date | null;
}

export const useInfrastructureData = (
  options: UseInfrastructureDataOptions = {}
): UseInfrastructureDataReturn => {
  const {
    autoRefresh = true,
    refreshInterval = 60000 // 60 seconds
  } = options;

  const [data, setData] = useState<InfrastructureData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<InfrastructureError | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Real accounts will be fetched from backend API
  const [connectedAccounts, setConnectedAccounts] = useState<InfrastructureData['accounts']>({
    aws: [],
    gcp: [],
    github: []
  });

  const fetchData = useCallback(async (): Promise<void> => {
    if (!isMountedRef.current) return;

    // Abort any in-flight request before starting a new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);

      console.log('Infrastructure Tab: Starting data fetch...');

      // First fetch connected accounts
      const token = localStorage.getItem('auth_token');
      if (!token) {
        console.warn('Infrastructure Tab: No auth token for accounts fetch');
        throw new Error('Authentication required - no auth token found');
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}/api/accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: abortControllerRef.current.signal
      });

      let currentAccounts: { aws: any[], gcp: any[], github: any[] } = { aws: [], gcp: [], github: [] };
      
      if (response.ok) {
        const result = await response.json();
        const accounts = result.accounts || [];
        
        currentAccounts = {
          aws: accounts.filter((acc: any) => acc.type === 'aws').map((acc: any) => ({
            id: acc.id,
            name: acc.name,
            type: acc.type,
            status: acc.status,
            region: acc.region || 'us-east-1',
            accountId: acc.accountId,
            lastSync: acc.lastSync
          })),
          gcp: accounts.filter((acc: any) => acc.type === 'gcp').map((acc: any) => ({
            id: acc.id,
            name: acc.name,
            type: acc.type,
            status: acc.status,
            region: acc.region || 'us-central1',
            accountId: acc.accountId,
            lastSync: acc.lastSync
          })),
          github: accounts.filter((acc: any) => acc.type === 'github').map((acc: any) => ({
            id: acc.id,
            name: acc.name,
            type: acc.type,
            status: acc.status,
            accountId: acc.accountId,
            lastSync: acc.lastSync
          }))
        };

        setConnectedAccounts(currentAccounts);
        console.log('Infrastructure Tab: Connected accounts fetched', {
          awsCount: currentAccounts.aws.length,
          gcpCount: currentAccounts.gcp.length,
          githubCount: currentAccounts.github.length
        });
      } else {
        console.error('Infrastructure Tab: Failed to fetch connected accounts', response.status);
      }
      
      console.log('Infrastructure Tab: Connected accounts after fetch:', currentAccounts);
      
      // Add overall timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Overall fetch timeout - 45 seconds')), 45000);
      });

      console.log('Infrastructure Tab: Auth token check', { 
        hasToken: !!token, 
        tokenLength: token?.length, 
        tokenPrefix: token?.substring(0, 10) 
      });

      // Fetch data from services that have connected accounts
      console.log('Infrastructure Tab: Starting API calls...');
      console.log('Infrastructure Tab: Connected accounts check:', { 
        awsAccounts: currentAccounts.aws.length,
        gcpAccounts: currentAccounts.gcp.length,
        githubAccounts: currentAccounts.github.length
      });
      
      // Wrap each service call with timeout
      const createTimeoutPromise = (promise: Promise<any>, serviceName: string, timeoutMs: number) => {
        return Promise.race([
          promise,
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error(`${serviceName} service timeout after ${timeoutMs}ms`)), timeoutMs)
          )
        ]);
      };

      // Only call services that have connected accounts
      const servicePromises = [];
      
      if (currentAccounts.aws.length > 0) {
        console.log('Infrastructure Tab: AWS accounts found, fetching all', currentAccounts.aws.length, 'accounts');
        // Fetch data from ALL AWS accounts and merge
        const allAwsPromise = (async () => {
          const accountResults = await Promise.allSettled(
            currentAccounts.aws.map((acc: any) => {
              console.log(`Infrastructure Tab: Fetching AWS data for account: ${acc.name} (${acc.id})`);
              return createTimeoutPromise(awsService.getAllData(acc.id), `AWS-${acc.name}`, 30000);
            })
          );

          // Merge all account data into one
          const merged = {
            ec2: { instances: [] as any[], total: 0, running: 0, stopped: 0 },
            rds: { instances: [] as any[], total: 0, available: 0, maintenance: 0 },
            s3: { buckets: [] as any[], totalSize: '0 B', totalObjects: 0 },
            lambda: { functions: [] as any[], total: 0, active: 0 },
            eks: { clusters: [] as any[], total: 0, active: 0 },
            cost: { total: 0, byService: {} as any, trend: 'stable' as const, byAccount: {} as Record<string, number>, byServicePerAccount: {} as Record<string, any> },
            accountPermissions: {} as Record<string, string[]>
          };

          accountResults.forEach((result, idx) => {
            const accName = currentAccounts.aws[idx]?.name || `Account ${idx}`;
            if (result.status === 'fulfilled' && result.value) {
              const d = result.value;
              console.log(`Infrastructure Tab: ✅ ${accName} data: EC2=${d.ec2?.total || 0}, RDS=${d.rds?.total || 0}, S3=${d.s3?.buckets?.length || 0}, Lambda=${d.lambda?.total || 0}`);

              // Collect missing permissions per account
              if (d.missingPermissions && d.missingPermissions.length > 0) {
                merged.accountPermissions[accName] = d.missingPermissions;
                console.warn(`Infrastructure Tab: ⚠️ ${accName} missing permissions:`, d.missingPermissions);
              }

              // Tag each resource with account name for filtering
              const tagWithAccount = (items: any[]) => items.map((item: any) => ({ ...item, accountName: accName }));

              merged.ec2.instances.push(...tagWithAccount(d.ec2?.instances || []));
              merged.ec2.total += d.ec2?.total || 0;
              merged.ec2.running += d.ec2?.running || 0;
              merged.ec2.stopped += d.ec2?.stopped || 0;
              merged.rds.instances.push(...tagWithAccount(d.rds?.instances || []));
              merged.rds.total += d.rds?.total || 0;
              merged.rds.available += d.rds?.available || 0;
              merged.s3.buckets.push(...tagWithAccount(d.s3?.buckets || []));
              merged.lambda.functions.push(...tagWithAccount(d.lambda?.functions || []));
              merged.lambda.total += d.lambda?.total || 0;
              merged.lambda.active += d.lambda?.active || 0;
              merged.eks.clusters.push(...tagWithAccount(d.eks?.clusters || []));
              merged.eks.total += d.eks?.total || 0;
              merged.eks.active += d.eks?.active || 0;
              merged.cost.total += d.cost?.total || 0;
              merged.cost.byAccount[accName] = d.cost?.total || 0;
              merged.cost.byServicePerAccount[accName] = d.cost?.byService || {};
              // Merge byService across accounts
              if (d.cost?.byService) {
                Object.entries(d.cost.byService).forEach(([service, cost]) => {
                  merged.cost.byService[service] = (merged.cost.byService[service] || 0) + (cost as number);
                });
              }
            } else {
              console.warn(`Infrastructure Tab: ⚠️ ${accName} failed:`, result.status === 'rejected' ? result.reason : 'no data');
            }
          });

          console.log(`Infrastructure Tab: 🔄 Merged totals: EC2=${merged.ec2.total}, RDS=${merged.rds.total}, S3=${merged.s3.buckets.length}, Lambda=${merged.lambda.total}, EKS=${merged.eks.total}`);
          console.log(`Infrastructure Tab: 🔑 Permission issues:`, merged.accountPermissions);
          return merged;
        })();
        servicePromises.push(allAwsPromise);
      } else {
        console.warn('Infrastructure Tab: No AWS accounts found');
        servicePromises.push(Promise.reject(new Error('No AWS accounts connected. Please connect an AWS account in Connected Accounts to view AWS infrastructure data.')));
      }
      
      if (currentAccounts.gcp.length > 0) {
        console.log('Infrastructure Tab: GCP accounts found, calling GCP service');
        servicePromises.push(createTimeoutPromise(gcpService.getAllData(), 'GCP', 10000));
      } else {
        console.warn('Infrastructure Tab: No GCP accounts found');
        servicePromises.push(Promise.reject(new Error('No GCP accounts connected. Please connect a Google Cloud Platform account in Connected Accounts to view GCP infrastructure data.')));
      }
      
      if (currentAccounts.github.length > 0) {
        console.log('Infrastructure Tab: GitHub accounts found, calling GitHub service');
        servicePromises.push(createTimeoutPromise(githubService.getAllData(), 'GitHub', 10000));
      } else {
        console.warn('Infrastructure Tab: No GitHub accounts found');
        servicePromises.push(Promise.reject(new Error('No GitHub accounts connected. Please connect a GitHub account in Connected Accounts to view GitHub infrastructure data.')));
      }

      const dataFetchPromise = Promise.allSettled(servicePromises);
      
      const [awsDataResult, gcpDataResult, githubDataResult] = await Promise.race([
        dataFetchPromise,
        timeoutPromise
      ]) as any;
      
      console.log('Infrastructure Tab: Promise.allSettled completed!');

      console.log('Infrastructure Tab: API calls completed');

      console.log('Infrastructure Tab: Service results:', {
        aws: awsDataResult.status,
        gcp: gcpDataResult.status,
        github: githubDataResult.status
      });

      // Log detailed results for debugging
      if (awsDataResult.status === 'fulfilled') {
        console.log('Infrastructure Tab: AWS data received:', awsDataResult.value);
      } else {
        if (currentAccounts.aws.length === 0) {
          console.warn('Infrastructure Tab: AWS - No accounts connected');
        } else {
          console.error('Infrastructure Tab: AWS failed:', awsDataResult.reason);
        }
      }

      if (gcpDataResult.status === 'fulfilled') {
        console.log('Infrastructure Tab: GCP data received:', gcpDataResult.value);
      } else {
        if (currentAccounts.gcp.length === 0) {
          console.warn('Infrastructure Tab: GCP - No accounts connected');
        } else {
          console.error('Infrastructure Tab: GCP failed:', gcpDataResult.reason);
        }
      }

      if (githubDataResult.status === 'fulfilled') {
        console.log('Infrastructure Tab: GitHub data received:', githubDataResult.value);
      } else {
        if (currentAccounts.github.length === 0) {
          console.warn('Infrastructure Tab: GitHub - No accounts connected');
        } else {
          console.error('Infrastructure Tab: GitHub failed:', githubDataResult.reason);
        }
      }

      // Handle results and show partial data even if some services fail
      console.log('Infrastructure Tab: Promise results before processing:', {
        awsStatus: awsDataResult.status,
        gcpStatus: gcpDataResult.status,
        githubStatus: githubDataResult.status
      });
      
      const awsData = awsDataResult.status === 'fulfilled' ? awsDataResult.value : getEmptyAWSData();
      const gcpData = gcpDataResult.status === 'fulfilled' ? gcpDataResult.value : getEmptyGCPData();
      const githubData = githubDataResult.status === 'fulfilled' ? githubDataResult.value : getEmptyGitHubData();

      console.log('Infrastructure Tab: Processed data', {
        awsData: {
          ec2Total: awsData?.ec2?.total,
          rdsTotal: awsData?.rds?.total,
          s3Buckets: awsData?.s3?.buckets?.length,
          lambdaTotal: awsData?.lambda?.total,
          eksTotal: awsData?.eks?.total,
          fullAwsData: awsData
        },
        gcpData: {
          gceTotal: gcpData?.gce?.total,
          gksTotal: gcpData?.gks?.total,
          fullGcpData: gcpData
        },
        githubData: {
          repoTotal: githubData?.repositories?.total,
          fullGithubData: githubData
        }
      });

      // Check for errors and show them clearly
      const errors = [awsDataResult, gcpDataResult, githubDataResult]
        .filter(result => result.status === 'rejected')
        .map(result => (result as PromiseRejectedResult).reason);

      if (errors.length > 0) {
        console.error('Infrastructure Tab: SERVICE ERRORS:', errors);
        errors.forEach((error, index) => {
          const service = ['AWS', 'GCP', 'GitHub'][index];
          console.error(`${service} Error:`, error);
        });
        
        // If AWS failed completely, throw the error
        if (awsDataResult.status === 'rejected') {
          console.error('Infrastructure Tab: AWS failed completely, throwing error');
          throw awsDataResult.reason;
        }
      } else {
        console.log('Infrastructure Tab: All services loaded successfully');
      }

      // Calculate summary data
      const summary: InfrastructureSummary = {
        totalResources: (
          (awsData?.ec2?.total || 0) +
          (awsData?.rds?.total || 0) +
          (awsData?.s3?.buckets?.length || 0) +
          (awsData?.lambda?.total || 0) +
          (awsData?.eks?.total || 0) +
          (gcpData?.gce?.total || 0) +
          (gcpData?.gks?.total || 0) +
          (githubData?.repositories?.total || 0)
        ),
        totalCost: (awsData?.cost?.total || 0) + (gcpData?.cost?.total || 0),
        healthScore: calculateHealthScore(awsData, gcpData, githubData),
        utilizationRate: calculateUtilization(awsData, gcpData),
        activeAlerts: Math.floor(Math.random() * 5), // Mock data
        serviceCount: {
          aws: Object.keys(awsData || {}).length - 1, // Exclude 'cost' key
          gcp: Object.keys(gcpData || {}).length - 1, // Exclude 'cost' key
          github: Object.keys(githubData || {}).length
        }
      };

      const infrastructureData: InfrastructureData = {
        summary,
        accounts: currentAccounts,
        aws: awsData || getEmptyAWSData(),
        gcp: gcpData || getEmptyGCPData(),
        github: githubData || getEmptyGitHubData(),
        lastUpdated: new Date().toISOString()
      };

      console.log('Infrastructure Tab: Final data being set', {
        summary: infrastructureData.summary,
        dataKeys: Object.keys(infrastructureData),
        isMounted: isMountedRef.current,
        awsDataActual: {
          ec2Total: infrastructureData.aws.ec2.total,
          awsAccountsCount: infrastructureData.accounts.aws.length
        }
      });

      if (isMountedRef.current) {
        setData(infrastructureData);
        setLastUpdated(new Date());
        console.log('Infrastructure Tab: Data successfully set to state');
      } else {
        console.warn('Infrastructure Tab: Component unmounted, not setting data');
      }

    } catch (error: any) {
      // Silently ignore aborted requests — not a real error
      if (error?.name === 'AbortError') return;

      console.error('Infrastructure Tab: Critical error fetching data:', error);

      // Show the real error to the user
      if (isMountedRef.current) {
        setError({
          code: error.code || 'FETCH_ERROR',
          message: error.message || 'Failed to fetch infrastructure data',
          service: error.service || 'multiple',
          timestamp: new Date().toISOString()
        });
        setData(null); // No fallback data
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        console.log('Infrastructure Tab: Loading state set to false');
      }
    }
  }, []);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    if (autoRefresh && refreshInterval > 0) {
      refreshTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          fetchData();
        }
      }, refreshInterval);
    }
  }, [autoRefresh, refreshInterval, fetchData]);

  const refetch = useCallback(async (): Promise<void> => {
    // Clear existing timeout before manual refresh
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    await fetchData();
    
    // Schedule next refresh
    scheduleRefresh();
  }, [fetchData, scheduleRefresh]);

  // Initial data fetch
  useEffect(() => {
    console.log('Infrastructure Tab: useEffect triggered, starting initial fetch');
    
    // Reset mounted ref to true
    isMountedRef.current = true;
    
    // Start loading immediately
    setLoading(true);
    
    // Fetch real data - let fetchData handle errors internally
    fetchData().finally(() => {
      console.log('Infrastructure Tab: Initial fetch completed (success or error)');
      if (isMountedRef.current) {
        scheduleRefresh();
      }
    });

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      // Don't set isMountedRef to false here - let the component cleanup handle it
    };
  }, []); // Remove dependencies to prevent infinite loop

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    data,
    loading,
    error,
    refetch,
    lastUpdated
  };
};

// Helper functions
function calculateHealthScore(awsData: any, gcpData: any, githubData: any): number {
  let totalResources = 0;
  let healthyResources = 0;

  if (awsData) {
    totalResources += (awsData.ec2?.total || 0) + (awsData.rds?.total || 0);
    healthyResources += (awsData.ec2?.running || 0) + (awsData.rds?.available || 0);
  }

  if (gcpData) {
    totalResources += (gcpData.gce?.total || 0) + (gcpData.gks?.total || 0);
    healthyResources += (gcpData.gce?.running || 0) + (gcpData.gks?.active || 0);
  }

  if (githubData) {
    totalResources += githubData.workflows?.total || 0;
    healthyResources += githubData.workflows?.active || 0;
  }

  return totalResources > 0 ? Math.round((healthyResources / totalResources) * 100) : 100;
}

function calculateUtilization(awsData: any, gcpData: any): number {
  let totalInstances = 0;
  let runningInstances = 0;

  if (awsData) {
    totalInstances += awsData.ec2?.total || 0;
    runningInstances += awsData.ec2?.running || 0;
  }

  if (gcpData) {
    totalInstances += gcpData.gce?.total || 0;
    runningInstances += gcpData.gce?.running || 0;
  }

  return totalInstances > 0 ? Math.round((runningInstances / totalInstances) * 100) : 0;
}

function getEmptyAWSData(): any {
  return {
    ec2: { instances: [], total: 0, running: 0, stopped: 0 },
    rds: { instances: [], total: 0, available: 0, maintenance: 0 },
    s3: { buckets: [], totalSize: '0 B', totalObjects: 0 },
    lambda: { functions: [], total: 0, active: 0 },
    eks: { clusters: [], total: 0, active: 0 },
    cost: { total: 0, byService: {}, trend: 'stable' as const }
  };
}

function getEmptyGCPData(): any {
  return {
    gce: { instances: [], total: 0, running: 0, stopped: 0 },
    gks: { clusters: [], total: 0, active: 0 },
    bigquery: { datasets: 0, tables: 0, totalSize: '0 B' },
    cost: { total: 0, byService: {}, trend: 'stable' as const }
  };
}

function getEmptyGitHubData(): any {
  return {
    repositories: { list: [], total: 0, public: 0, private: 0 },
    workflows: { total: 0, active: 0, successful: 0, failed: 0 },
    stats: { commits: 0, pullRequests: 0, issues: 0 }
  };
}